import React from 'react';
import { Button, Typography } from 'antd';
import { ShoppingCartOutlined, EyeOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addToCart } from '../../store/slices/cartSlice';
import { formatPrice } from '../../utils/formatters';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { animateCartFly } from '../../utils/animations';

const { Text, Title } = Typography;

const ProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleAddToCart = (e) => {
    e.stopPropagation();
    animateCartFly(e.clientX, e.clientY);
    dispatch(addToCart(product));
  };

  const handleClick = () => {
    navigate(`/products/${product.id}`);
  };

  const price = parseFloat(product.discount_price || product.price);
  const originalPrice = product.discount_price ? parseFloat(product.price) : null;
  const discount = originalPrice
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : null;

  const getImageUrl = () => {
    return product.image_urls && product.image_urls.length > 0
      ? `${process.env.REACT_APP_API_BASE_URL}${product.image_urls[0]}`
      : 'https://via.placeholder.com/300x300?text=No+Image';
  };

  return (
    <motion.div
      whileHover={{ y: -10, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      style={{ height: '100%', cursor: 'pointer' }}
      onClick={handleClick}
    >
      <div
        style={{
          background: '#ffffff',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 24,
          overflow: 'hidden',
          position: 'relative',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Cover Image Area */}
        <div style={{ position: 'relative', height: 220, overflow: 'hidden', background: '#f8fafc', padding: '16px 16px 0 16px' }}>
          <motion.img
            whileHover={{ scale: 1.08 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            alt={product.name}
            src={getImageUrl()}
            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '16px 16px 0 0', filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.1))' }}
          />
          {/* Overlay Actions on Hover */}
          <div className="card-overlay-actions" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.2)', opacity: 0, transition: 'opacity 0.3s', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button shape="circle" size="large" icon={<EyeOutlined />} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', backdropFilter: 'blur(4px)' }} />
          </div>
          
          {/* Discount Badge */}
          {discount && (
            <div
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                background: 'linear-gradient(135deg, #ef4444 0%, #f43f5e 100%)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: 20,
                fontWeight: 800,
                fontSize: '0.85rem',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              <Sparkles size={14} /> -{discount}%
            </div>
          )}
          
          {/* Availability Badge */}
          {!product.is_available || product.stock_quantity <= 0 ? (
            <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(239, 68, 68, 0.8)', color: 'white', padding: '4px 12px', borderRadius: 20, fontWeight: 700, fontSize: '0.8rem', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.2)' }}>
              Tugadi
            </div>
          ) : (
            <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(16, 185, 129, 0.8)', color: 'white', padding: '4px 12px', borderRadius: 20, fontWeight: 700, fontSize: '0.8rem', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.2)' }}>
              Bor
            </div>
          )}
        </div>

        {/* Content Area */}
        <div style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', flex: 1, background: '#ffffff' }}>
          <div style={{ flex: 1 }}>
            <Title level={5} style={{ margin: '0 0 6px 0', fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.05rem', color: '#0f172a', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {product.name}
            </Title>
            
            <Text type="secondary" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: '0.85rem', marginBottom: 12, color: '#475569' }}>
              {product.description}
            </Text>
          </div>

          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16 }}>
              {originalPrice && (
                <Text delete style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: 600 }}>
                  {formatPrice(originalPrice)}
                </Text>
              )}
              <Title level={4} style={{ margin: 0, background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 900, fontSize: '1.25rem', fontFamily: 'Outfit' }}>
                {formatPrice(price)}
              </Title>
            </div>

            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                type="primary"
                onClick={handleAddToCart}
                disabled={!product.is_available || product.stock_quantity <= 0}
                style={{
                  width: '100%',
                  height: 40,
                  borderRadius: 12,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  background: (!product.is_available || product.stock_quantity <= 0) ? '#e2e8f0' : 'linear-gradient(135deg, #0ea5e9 0%, #8b5cf6 100%)',
                  border: 'none',
                  boxShadow: (!product.is_available || product.stock_quantity <= 0) ? 'none' : '0 6px 16px rgba(139, 92, 246, 0.25)',
                  color: (!product.is_available || product.stock_quantity <= 0) ? '#94a3b8' : '#ffffff'
                }}
              >
                <ShoppingCartOutlined style={{ fontSize: 18 }} />
                {product.is_available && product.stock_quantity > 0 ? "Savatga" : 'Tugagan'}
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
